import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";
import { fmt } from "@/lib/format";
import {
  validateCreditAdjust, signedAmount, computeExpectedBalance, isRiskyAdjustment,
} from "@/lib/creditAdjust";

// 모바일 크레딧 조정 bottom sheet (P3, 2026-09-17).
// desktop 과 동일 API(adminAdjustCredit) + 동일 순수 로직(lib/creditAdjust) 재사용.
// 실행 전 현재/변경/변경후 잔액 명시 + confirmation. STG 는 조회 전용(disabled).
export default function MobileCreditAdjustSheet({ user, onClose, onSuccess }) {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  const [sign, setSign] = useState(1);       // 1 지급 | -1 회수
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose, submitting]);

  const balance = user.balance ?? 0;
  const { validMag, validReason, valid } = validateCreditAdjust({ mag: amount, reason });
  const signed = signedAmount(sign, amount);
  const expected = validMag ? computeExpectedBalance(balance, signed) : null;
  const risky = isRiskyAdjustment({ mag: amount, expected });

  async function submit() {
    if (readOnly) { setError("STG 조회 상태에서는 크레딧 조정이 비활성화됩니다."); return; }
    if (!valid) { setError(!validMag ? "1 이상의 정수를 입력하세요." : "사유를 3자 이상 입력하세요."); return; }
    if (risky && !confirming) { setConfirming(true); setError(null); return; }
    setSubmitting(true);
    setError(null);
    try {
      await analyticsApi.adminAdjustCredit(user.userId, { amount: signed, reason: reason.trim() });
      onSuccess?.({ userId: user.userId, amount: signed, newBalance: expected });
      onClose?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={() => !submitting && onClose?.()} aria-hidden="true" />
      <div className="relative w-full rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[15px] font-bold text-ink-900">크레딧 조정</h3>
          <button type="button" onClick={() => !submitting && onClose?.()} aria-label="닫기"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="truncate text-[13px] text-ink-500">{user.email || user.userId}</div>

        {readOnly ? (
          <div className="mt-3 rounded-xl bg-ink-100 px-3.5 py-3 text-[13px] font-medium text-ink-500">
            STG 조회 전용 — 크레딧 조정이 비활성화되어 있습니다.
          </div>
        ) : (
          <>
            {/* 지급/회수 토글 */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => { setSign(1); setConfirming(false); }}
                className={`h-11 rounded-xl border text-[14px] font-semibold ${sign === 1 ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-ink-200 bg-white text-ink-500"}`}>
                지급 (+)
              </button>
              <button type="button" onClick={() => { setSign(-1); setConfirming(false); }}
                className={`h-11 rounded-xl border text-[14px] font-semibold ${sign === -1 ? "border-red-500 bg-red-50 text-red-600" : "border-ink-200 bg-white text-ink-500"}`}>
                회수 (−)
              </button>
            </div>

            <input
              inputMode="numeric"
              value={amount}
              onChange={(e) => { setAmount(e.target.value.replace(/[^0-9]/g, "")); setConfirming(false); }}
              placeholder="조정 NC (정수)"
              className="mt-2 h-11 w-full rounded-xl border border-ink-200 px-3 text-sm text-ink-800 focus:border-brand-400 focus:outline-none"
            />
            <input
              value={reason}
              onChange={(e) => { setReason(e.target.value); setConfirming(false); }}
              placeholder="사유 (3자 이상, 필수)"
              className="mt-2 h-11 w-full rounded-xl border border-ink-200 px-3 text-sm text-ink-800 focus:border-brand-400 focus:outline-none"
            />

            {/* before / after */}
            <div className={`mt-3 rounded-xl border px-3.5 py-3 text-[13px] tabular-nums ${risky ? "border-red-300 bg-red-50" : "border-ink-200 bg-ink-50/60"}`}>
              <div className="flex justify-between"><span className="text-ink-500">현재 잔액</span><span className="font-medium text-ink-800">{fmt.nc(balance)}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">변경</span><span className={`font-medium ${sign === 1 ? "text-emerald-600" : "text-red-600"}`}>{validMag ? `${signed > 0 ? "+" : ""}${fmt.nc(signed)}` : "—"}</span></div>
              <div className="mt-1 flex justify-between border-t border-ink-200 pt-1"><span className="text-ink-500">변경 후</span><span className={`font-bold ${expected != null && expected < 0 ? "text-red-600" : "text-ink-900"}`}>{expected != null ? fmt.nc(expected) : "—"}</span></div>
            </div>

            {risky && (
              <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[12px] font-medium text-red-600">
                {(expected != null && expected < 0) ? "변경 후 잔액이 음수가 됩니다." : "대액 조정입니다."} 한 번 더 확인하세요.
              </div>
            )}
            {error && <div className="mt-2 text-[12px] text-red-600">{error}</div>}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => !submitting && onClose?.()} disabled={submitting}
                className="h-12 rounded-xl border border-ink-200 bg-white text-[14px] font-semibold text-ink-600 disabled:opacity-50">
                취소
              </button>
              <button type="button" onClick={submit} disabled={submitting || !valid}
                className={`h-12 rounded-xl text-[14px] font-semibold text-white disabled:opacity-50 ${risky ? "bg-red-600" : "bg-brand-600"}`}>
                {submitting ? "처리 중…" : confirming ? "정말 변경" : "변경 확인"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
